import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import type Polygon from '@arcgis/core/geometry/Polygon.js';
import {
  registerHandle,
  resolve,
  summarizeRef,
  type ResolvedRef,
  type Source,
  SourceSchema,
} from '@/agent/refs';
import type { Tool } from './types';

type Input = { source: Source; radius_meters: number };

export const computeBuffer: Tool<Input> = {
  name: 'compute_buffer',
  description:
    'Compute a geodesic buffer (in meters) around a Source. Returns a polygon handle plus the buffer area in square meters. No map side-effects — pair with `render` to draw it.',
  input_schema: {
    type: 'object',
    properties: {
      source: SourceSchema,
      radius_meters: { type: 'number', exclusiveMinimum: 0 },
    },
    required: ['source', 'radius_meters'],
    additionalProperties: false,
  },
  execute: async ({ source, radius_meters }) => {
    const ref = await resolve(source);
    if (!ref.geometry) throw new Error('compute_buffer: source has no geometry');
    const buffered = geometryEngine.geodesicBuffer(
      ref.geometry as Parameters<typeof geometryEngine.geodesicBuffer>[0],
      radius_meters,
      'meters',
    ) as Polygon | Polygon[] | null;
    const polygon = Array.isArray(buffered) ? buffered[0] : buffered;
    if (!polygon) throw new Error('compute_buffer: buffer computation produced no geometry');

    const newRef: ResolvedRef = {
      source_kind: 'geometry',
      geometry: polygon,
      extent: polygon.extent ?? undefined,
    };
    const handle = registerHandle(newRef);
    const area = geometryEngine.geodesicArea(polygon, 'square-meters');
    return {
      handle,
      summary: summarizeRef(newRef),
      area_sq_m: area,
      radius_meters,
    };
  },
};
